import { createContext, useContext, JSX, ReactNode } from 'react';
import { useTokenCostData } from '../hooks/token-history-graph.hooks';
import { TokenCostData } from '../../../../types/dashboard';

/**
 * One fetch of the cost report, shared by every card that shows part of it.
 *
 * Two cards want these figures: the spend card, and the budget card's all-time line. Calling the
 * hook in both would fetch twice, and the endpoint walks the whole token history to work out the
 * all-time total, so that is not a free duplicate and it gets less free as the table grows.
 *
 * The visible reason matters more. Each card has its own reload button. With separate fetches,
 * reloading one would leave the other showing the same figure from a different moment, which is
 * the sort of disagreement nobody thinks to distrust until it has already misled them.
 */

export type TokenCostState = {
    data: TokenCostData | null,
    loading: boolean,
    error: string | null,
    reloadData: () => boolean,
}

const TokenCostContext = createContext<TokenCostState | null>(null);

type TokenCostProviderProps = {
    children: ReactNode,
}

export const TokenCostProvider: (props: TokenCostProviderProps) => JSX.Element = (
    { children }: TokenCostProviderProps
): JSX.Element => {
    const state: TokenCostState = useTokenCostData();

    return (
        <TokenCostContext.Provider value={ state }>
            { children }
        </TokenCostContext.Provider>
    );
}

/**
 * Throws rather than returning a null-shaped default, because a card rendered outside the provider
 * would otherwise show a confident zero. On this screen a wrong zero is the one failure that does
 * not look like a failure.
 */
export const useSharedTokenCost: () => TokenCostState = (): TokenCostState => {
    const state: TokenCostState | null = useContext(TokenCostContext);

    if (state === null) {
        throw new Error('useSharedTokenCost must be used inside a TokenCostProvider.');
    }

    return state;
}
